import { lazy, Suspense, useEffect, useState, type ReactElement } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'

import { OrgChoicePage } from '@nebula/shared/components/OrgChoicePage'
import { portalHubApi } from '@nebula/shared/services'
import { useAuthStore } from '@nebula/shared/store'

import LoginPage from './pages/LoginPage'

const PublicLayout = lazy(() => import('./layouts/PublicLayout'))
const MainLayout = lazy(() => import('./layouts/MainLayout'))
const StatusPage = lazy(() => import('./pages/StatusPage'))
const RoadmapPage = lazy(() => import('./pages/RoadmapPage'))
const SpacesPage = lazy(() => import('./pages/SpacesPage'))
const SpaceDetailPage = lazy(() => import('./pages/SpaceDetailPage'))
const NewIssuePage = lazy(() => import('./pages/NewIssuePage'))
const IssueDetailPage = lazy(() => import('./pages/IssueDetailPage'))
const EditIssuePage = lazy(() => import('./pages/EditIssuePage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const MyIssuesPage = lazy(() => import('./pages/MyIssuesPage'))
const ChangelogPage = lazy(() => import('./pages/ChangelogPage'))
const KnowledgeBasePage = lazy(() => import('./pages/KnowledgeBasePage'))
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'))

function RouteLoadingFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <div className="text-center">
        <div className="mx-auto mb-3 size-7 animate-spin rounded-full border-b-2 border-teal-600" />
        <p className="text-sm text-gray-600">Carregando...</p>
      </div>
    </div>
  )
}

function withRouteFallback(element: ReactElement) {
  return <Suspense fallback={<RouteLoadingFallback />}>{element}</Suspense>
}

function AccessDeniedPage() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100">
      <div className="max-w-md px-6 text-center">
        <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-red-100">
          <svg
            className="size-8 text-red-600"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636"
            />
          </svg>
        </div>
        <h1 className="mb-2 text-2xl font-bold text-gray-900">Acesso Restrito</h1>
        <p className="mb-6 text-gray-600">
          Sua organização não tem permissão para acessar o Nexxus Hub. Entre em contato com o
          administrador da plataforma.
        </p>
        <div className="flex items-center justify-center gap-3">
          <a
            href="/"
            className="rounded-lg bg-teal-50 px-4 py-2 text-sm font-medium text-teal-700 transition-colors hover:bg-teal-100"
          >
            Ir para Status
          </a>
          <button
            onClick={() => useAuthStore.getState().logout()}
            className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
          >
            Fazer Logout
          </button>
        </div>
      </div>
    </div>
  )
}

function App() {
  const { user, checkAuth, isAuthenticated, isLoading, memberships, orgChoiceRequired } =
    useAuthStore()
  const location = useLocation()

  const [hubAccessAllowed, setHubAccessAllowed] = useState<boolean | null>(null)

  const isPublicRoute =
    location.pathname === '/' ||
    location.pathname === '/status' ||
    location.pathname.startsWith('/status/')
  const isLoginPage = location.pathname === '/login'

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  // Check hub access after authentication
  useEffect(() => {
    // Sem org escolhida (#1145) não há X-Organization-Id: o backend recusaria a chamada
    if (!isAuthenticated || !user || orgChoiceRequired) {
      setHubAccessAllowed(null)
      return
    }

    const allPerms = memberships.flatMap((m: { permissions: string[] }) => m.permissions)
    const hasHubPerm = allPerms.some((p: string) => p.startsWith('hub:'))

    portalHubApi
      .getHubConfig()
      .then((config) => {
        if (hasHubPerm) {
          setHubAccessAllowed(true)
          return
        }
        const orgType = user.organization?.type
        setHubAccessAllowed(orgType ? config.allowed_org_types.includes(orgType) : false)
      })
      .catch(() => {
        // If the config endpoint fails, default to allowed (graceful degradation)
        setHubAccessAllowed(true)
      })
  }, [isAuthenticated, user, memberships, orgChoiceRequired])

  // Public routes always accessible (status is the landing page)
  if (isPublicRoute) {
    return (
      <Routes>
        <Route element={withRouteFallback(<PublicLayout />)}>
          <Route path="/" element={withRouteFallback(<StatusPage />)} />
          <Route path="/status" element={withRouteFallback(<StatusPage />)} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    )
  }

  // Show loading while checking auth (but NOT on login page)
  if (isLoading && !isLoginPage) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="mx-auto mb-4 size-8 animate-spin rounded-full border-b-2 border-teal-600"></div>
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    )
  }

  // Not authenticated — show login or redirect
  if (!isAuthenticated || !user) {
    if (isLoginPage) {
      return <LoginPage />
    }
    return <Navigate to="/login" replace />
  }

  // Mais de uma org compatível e a aba não escolheu: pergunta antes de qualquer tela (#1145)
  if (orgChoiceRequired) {
    return <OrgChoicePage />
  }

  // Still checking hub access
  if (hubAccessAllowed === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="mx-auto mb-4 size-8 animate-spin rounded-full border-b-2 border-teal-600"></div>
          <p className="text-gray-600">Verificando acesso...</p>
        </div>
      </div>
    )
  }

  // Access denied
  if (!hubAccessAllowed) {
    return <AccessDeniedPage />
  }

  // Authenticated and allowed
  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/dashboard" replace />} />
      <Route path="/" element={withRouteFallback(<MainLayout />)}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={withRouteFallback(<DashboardPage />)} />
        <Route path="roadmap" element={withRouteFallback(<RoadmapPage />)} />
        <Route path="status" element={withRouteFallback(<StatusPage />)} />
        <Route path="spaces" element={withRouteFallback(<SpacesPage />)} />
        <Route path="spaces/:slug" element={withRouteFallback(<SpaceDetailPage />)} />
        <Route path="spaces/:slug/new" element={withRouteFallback(<NewIssuePage />)} />
        <Route path="spaces/:slug/issues/:id" element={withRouteFallback(<IssueDetailPage />)} />
        <Route path="spaces/:slug/issues/:id/edit" element={withRouteFallback(<EditIssuePage />)} />
        <Route path="my-issues" element={withRouteFallback(<MyIssuesPage />)} />
        <Route path="changelog" element={withRouteFallback(<ChangelogPage />)} />
        <Route path="knowledge-base" element={withRouteFallback(<KnowledgeBasePage />)} />
        <Route path="notifications" element={withRouteFallback(<NotificationsPage />)} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  )
}

export default App
